import { motion } from "framer-motion";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AIDA_DOCK_W, AidaSidebar, type AvvioChat } from "@/components/m3/AidaSidebar";
import { useMe } from "@/hooks/useMe";
import { useMyPermessi } from "@/hooks/usePermessi";
import { ImpostazioniOverlay } from "@/components/ImpostazioniOverlay";
import { TabModulo } from "@/components/TabModulo";
import { SEZIONI_M3, isMaitimeStaff } from "@/lib/nav-config";
import { hasPermessoSezione, isAzioneConcessa } from "@/lib/permessi";
import { cn } from "@/lib/utils";
import { CarichiPrevistiView } from "./M3/CarichiPrevistiView";
import { DocumentiView } from "./M3/DocumentiView";
import { DomandeView } from "./M3/DomandeView";
import { ImpostazioniView } from "./M3/ImpostazioniView";
import { MercePronteView } from "./M3/MercePronteView";

/**
 * Guscio M3 «Spedizioni e Logistica» — redesign #29 (SPEC_M3_modulo_v1).
 *
 * Tre sezioni indipendenti, nessuna dipende da `m3.carico` in UI:
 *  · Carichi previsti — il TABELLONE: dove Marina lavora. Prima e di default.
 *  · Documenti        — NDP→pacchetto documenti export→DDT.
 *  · Commesse pronte  — TODO (proposta carichi a Marina). SPEC dedicata.
 *
 * Ingranaggio Impostazioni fuori dalle tab; sidebar Aida persistente ma
 * collassata di default.
 */

type Tab = "documenti" | "previsti" | "pronte" | "domande";

// Le sezioni che vivono in QUESTA pagina, con la chiave di permesso che le
// governa. Le altre due del modulo — Calendario e Partenze — hanno una rotta
// propria: la fila delle pillole le comprende tutte e viene da `SEZIONI_M3` in
// `nav-config.ts`, che è anche ciò che disegna la barra laterale.
//
// «Carichi previsti» per prima e di default: è il TABELLONE, il posto dove Marina
// lavora ogni giorno. I documenti sono ciò che ne conseguono, non il punto di
// partenza.
const TABS: { id: Tab; permesso: string }[] = [
  { id: "previsti", permesso: "m3.carichi" },
  { id: "documenti", permesso: "m3.documenti" },
  { id: "pronte", permesso: "m3.pronte" },
  { id: "domande", permesso: "m3.domande" },
];

// ⚠️ DERIVATO da TABS, non scritto a mano (29-08-2026).
//
// Era una seconda lista, e aggiungendo «Domande di Aida» ho aggiornato `TABS` e
// il tipo ma non questa: `?tab=domande` risultava un indirizzo non valido e
// cadeva sul default, cioè **la tab apriva il tabellone**. Il difetto non si
// vedeva da nessuna parte — nessun errore, nessuna pillola spenta, solo il
// contenuto sbagliato — ed è sopravvissuto a tsc, a eslint e a una prova in
// browser che montava la vista in un banco senza attraversare la rotta.
//
// Con una sola fonte una sezione nuova non può più esistere a metà.
const TAB_VALIDI: Tab[] = TABS.map((t) => t.id);

const TAB_DEFAULT: Tab = "previsti";

export function M3SpedizioniPage() {
  const { data: me } = useMe();
  const isStaff = me ? isMaitimeStaff(me) : false;
  const { data: perms } = useMyPermessi();
  // Ingranaggio Impostazioni: staff (tutte le sezioni) oppure utenti col permesso
  // granulare di gestione associazioni email→cliente (vedono solo quella sezione).
  //
  // Dal 30-08-2026 quella sezione chiede anche `m3_conversazioni_vedi` (è un
  // elenco di indirizzi dei clienti): senza, l'ingranaggio aprirebbe un pannello
  // con dentro un 403 — una porta su una stanza chiusa.
  const puoImpostazioni =
    isStaff ||
    (isAzioneConcessa(perms, "m3_cliente_email_edit") &&
      isAzioneConcessa(perms, "m3_conversazioni_vedi"));

  // Le tab che questo ruolo può vedere. Chi ha una sola sezione atterra lì:
  // Enzo con le sole «Partenze» non attraversa il tabellone di Marina.
  const tabsVisibili = TABS.filter((t) => hasPermessoSezione(perms, t.permesso, "M3"));

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const chiesta = (TAB_VALIDI as string[]).includes(tabParam ?? "")
    ? (tabParam as Tab)
    : TAB_DEFAULT;
  const tab: Tab =
    tabsVisibili.some((t) => t.id === chiesta) || tabsVisibili.length === 0
      ? chiesta
      : tabsVisibili[0].id;
  // `?tab=previsti` era un indirizzo valido ma non canonico (il tabellone è la
  // sezione senza parametro): lo si normalizza, altrimenti nessuna pillola
  // risulterebbe accesa su un link vecchio.
  useEffect(() => {
    if (tabParam === TAB_DEFAULT) {
      const u = new URLSearchParams(searchParams);
      u.delete("tab");
      setSearchParams(u, { replace: true });
    }
  }, [tabParam, searchParams, setSearchParams]);

  const qc = useQueryClient();
  const [aidaOpen, setAidaOpen] = useState(false);
  // Avvio della chat da una domanda del tabellone: Aida ripete la sua domanda e
  // l'id viaggia col turno, così sa quale chiudere.
  const [aidaAvvio, setAidaAvvio] = useState<AvvioChat | undefined>();
  function apriChat(avvio: AvvioChat) {
    setAidaAvvio(avvio);
    setAidaOpen(true);
  }
  const [impostazioniAperte, setImpostazioniAperte] = useState(false);

  return (
    <>
      <div
        className={cn(
          "transition-[padding] duration-200",
          // Variabile CSS invece di padding inline: il dock esiste solo da `lg`
          // in su, e uno style inline si applicherebbe anche sotto, lasciando una
          // colonna vuota a destra su schermi piccoli.
          aidaOpen ? "lg:pr-[var(--aida-w)]" : "lg:pr-11",
        )}
        style={{ "--aida-w": `${AIDA_DOCK_W}px` } as React.CSSProperties}
      >
        {/* ⚠️ L'header del modulo (quadratino MP, «Modulo M3», «Spedizioni e
            Logistica», il pallino «Sistemi operativi») è uscito il 27-08-2026:
            era il terzo titolo dello stesso modulo — l'header del portale dice
            già dove sei, e le due pagine-sezione ne avevano uno ciascuna, di due
            misure diverse. Le tab sono ora dentro il contenuto, così stanno
            nell'area che si stringe quando il pannello di Aida si apre. */}

        {/* Contenuto */}
        <motion.main
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className={cn(
            "mx-auto px-6 py-7",
            // Il tabellone ha 11 colonne e il dock di Aida ne mangia 420: gli
            // serve tutta la larghezza. Le altre sezioni stanno meglio centrate.
            tab === "previsti" ? "max-w-none" : "max-w-7xl",
          )}
        >
          <TabModulo
            sezioni={SEZIONI_M3}
            modulo="M3"
            ingranaggioVisibile={puoImpostazioni}
            onIngranaggio={() => setImpostazioniAperte(true)}
          />

          {tab === "documenti" && <DocumentiView />}
          {tab === "domande" && (
            <DomandeView
              // `richiestaId` viaggia col turno: è così che Aida sa **quale**
              // domanda sta chiudendo quando Marina risponde conversando,
              // invece di trattarla come un messaggio qualsiasi.
              onApriChat={(d, testo) =>
                apriChat({
                  chiave: `domanda-${d.id}`,
                  // Non `d.testo`: quella è l'etichetta interna («Mi manca il
                  // riferimento: ddt»). La chat si apre con la domanda come Aida
                  // l'ha scritta, più una riga su da quale email viene.
                  domanda: testo,
                  richiestaId: d.id,
                })
              }
            />
          )}
          {tab === "previsti" && <CarichiPrevistiView onApriChat={apriChat} />}
          {tab === "pronte" && <MercePronteView />}
        </motion.main>
      </div>

      {/* Sidebar Aida: dock fisso a destra, persistente, collassato di default */}
      <div className="fixed bottom-0 right-0 top-14 z-30 hidden lg:block">
        <AidaSidebar
          open={aidaOpen}
          onToggle={setAidaOpen}
          avvio={aidaAvvio}
          // Una domanda chiusa dalla chat deve far sparire il badge dal tabellone.
          onAzioneCompletata={() => qc.invalidateQueries({ queryKey: ["m3"] })}
        />
      </div>

      {impostazioniAperte && (
        <ImpostazioniOverlay onChiudi={() => setImpostazioniAperte(false)}>
          <ImpostazioniView />
        </ImpostazioniOverlay>
      )}
    </>
  );
}
